/**
 * The step sequencer (07 §2): plays a `Song` on the WebAudio clock with look-ahead scheduling.
 * Tonal instruments are live oscillators with an envelope and an optional plucked low-pass;
 * percussion is the sound bank's drum buffers. Layers are switched at bar lines only, from a
 * mask the caller keeps up to date, so the score changes in time with itself.
 */

import { ENVELOPE_SLOTS, envelopePoints, instrument, type ToneInstrument, type ToneWave } from '../music/instruments';
import type { LayerMask } from '../music/layers';
import { DRUM_MIDI, type NoteEvent, type Song, STEPS_PER_BAR, type Track } from '../music/pattern';
import { collectDue, createClock, LOOKAHEAD, type SeqClock, skipMissed, stepTime } from '../music/schedule';
import { midiToHz, pulseCoefficients } from '../music/theory';
import type { Bank } from './bank';
import type { Graph } from './graph';

const NO_NOTES: readonly NoteEvent[] = [];
const FADE = 0.4;
/** Long enough for a faded song's last scheduled note to have started and died. */
const RETIRE_MS = 4000;
/** Waves WebAudio has built in; the pulse waves are periodic waves made in the constructor. */
const NATIVE: Partial<Record<ToneWave, OscillatorType>> = {
  saw: 'sawtooth', square: 'square', triangle: 'triangle', sine: 'sine',
};

interface Prepared {
  track: Track;
  /** Notes by the step they start on. */
  byStep: ReadonlyArray<readonly NoteEvent[]>;
  /** Whether this track sounds in the current bar: sampled from the mask at each bar line. */
  on: boolean;
}

export class Sequencer {
  /** This song's output. A new one per song, so the last song's tails fade on their own gain. */
  private out: GainNode;
  private readonly waves = new Map<ToneWave, PeriodicWave>();
  private readonly due: number[] = [];
  private readonly envelope = new Float64Array(ENVELOPE_SLOTS);
  private song: Song | null = null;
  private tracks: Prepared[] = [];
  private clock: SeqClock | null = null;
  private loop = false;
  private onEnd: (() => void) | null = null;
  /** Layers to sound; null = all of them (a plain song with no adaptive layers). */
  mask: LayerMask | null = null;

  constructor(private readonly graph: Graph, private readonly bank: Bank) {
    this.out = graph.ctx.createGain();
    this.out.connect(graph.music);
    for (const [name, duty] of [['pulse25', 0.25], ['pulse12', 0.125]] as const) {
      const { real, imag } = pulseCoefficients(duty, 48);
      this.waves.set(name, graph.ctx.createPeriodicWave(real, imag));
    }
  }

  get playing(): boolean {
    return this.song !== null;
  }

  get songName(): string | null {
    return this.song?.name ?? null;
  }

  play(song: Song, loop: boolean, onEnd: (() => void) | null = null): void {
    const now = this.graph.now;
    this.song = song;
    this.loop = loop;
    this.onEnd = onEnd;
    this.tracks = song.tracks.map((track) => {
      const byStep: Array<readonly NoteEvent[]> = new Array<readonly NoteEvent[]>(song.steps).fill(NO_NOTES);
      for (const n of track.notes) byStep[n.step] = [...(byStep[n.step] ?? NO_NOTES), n];
      return { track, byStep, on: false };
    });
    this.clock = createClock(now + 0.06, song.bpm);
    this.retire(now);
    this.out = this.graph.ctx.createGain();
    this.out.connect(this.graph.music);
  }

  /** Fade the current output and unplug it once its tails are over; the next song gets a fresh one. */
  private retire(now: number): void {
    const old = this.out;
    old.gain.cancelScheduledValues(now);
    old.gain.setTargetAtTime(0, now, FADE / 3);
    setTimeout(() => old.disconnect(), RETIRE_MS);
  }

  /** Let ringing notes finish under a short fade, and schedule nothing more. */
  stop(): void {
    if (!this.song) return;
    const now = this.graph.now;
    this.song = null;
    this.clock = null;
    this.onEnd = null;
    this.retire(now);
    this.out = this.graph.ctx.createGain();
    this.out.connect(this.graph.music);
  }

  /** Duck under a sting, or come back up. */
  duck(level: number): void {
    if (this.song) this.out.gain.setTargetAtTime(level, this.graph.now, 0.15);
  }

  /** Hand WebAudio every step inside the look-ahead window. Called from a timer AND from `update`. */
  tick(): void {
    const { song, clock } = this;
    if (!song || !clock) return;
    const now = this.graph.now;
    skipMissed(clock, now);
    this.due.length = 0;
    collectDue(clock, now, LOOKAHEAD, this.due);
    for (const absolute of this.due) {
      if (!this.loop && absolute >= song.steps) {
        const done = this.onEnd;
        this.song = null;
        this.clock = null;
        this.onEnd = null;
        done?.();
        return;
      }
      const step = absolute % song.steps;
      if (step % STEPS_PER_BAR === 0) for (const t of this.tracks) t.on = this.mask ? this.mask[t.track.layer] : true;
      const at = stepTime(clock, absolute);
      for (const t of this.tracks) {
        if (!t.on) continue;
        for (const n of t.byStep[step] ?? NO_NOTES) this.note(t.track, n, at, clock.stepSeconds);
      }
    }
  }

  private note(track: Track, n: NoteEvent, at: number, stepSeconds: number): void {
    const inst = instrument(track.instrument);
    const level = inst.gain * track.gain * n.vel;
    if (inst.kind === 'drum') {
      const ctx = this.graph.ctx;
      const src = ctx.createBufferSource();
      src.buffer = this.bank.next(inst.sfx);
      src.playbackRate.value = Math.pow(2, (n.midi - DRUM_MIDI) / 12);
      const g = ctx.createGain();
      g.gain.value = level;
      src.connect(g).connect(this.out);
      src.start(at);
      return;
    }
    this.tone(inst, midiToHz(n.midi), level, at, n.len * stepSeconds * 0.92, n.len);
  }

  private tone(inst: ToneInstrument, hz: number, level: number, at: number, seconds: number, steps: number): void {
    const ctx = this.graph.ctx;
    const amp = ctx.createGain();
    const voices = inst.detune === 0 ? 1 : 2;
    // Two oscillators sum: bring the pair back to about the loudness of one.
    const peak = level * (voices === 2 ? 0.6 : 1);
    const count = envelopePoints(inst, seconds, this.envelope);
    amp.gain.setValueAtTime(0, at);
    for (let i = 2; i < count; i += 2) {
      amp.gain.linearRampToValueAtTime((this.envelope[i + 1] ?? 0) * peak, at + (this.envelope[i] ?? 0));
    }
    const end = at + seconds + inst.release + 0.03;

    let head: AudioNode = amp;
    if (inst.cutoff !== undefined) {
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.Q.value = inst.q ?? 0.7;
      const open = Math.min(16000, inst.cutoff * (inst.cutoffEnv ?? 1));
      filter.frequency.setValueAtTime(open, at);
      if (open !== inst.cutoff) filter.frequency.setTargetAtTime(inst.cutoff, at, inst.decay / 2);
      filter.connect(amp);
      head = filter;
    }
    amp.connect(this.out);

    for (let v = 0; v < voices; v++) {
      const osc = ctx.createOscillator();
      const wave = this.waves.get(inst.wave);
      if (wave) osc.setPeriodicWave(wave);
      else osc.type = NATIVE[inst.wave] ?? 'sine';
      osc.frequency.value = hz;
      osc.detune.value = v === 0 ? 0 : inst.detune;
      if (inst.vibrato && steps >= inst.vibrato.minSteps) {
        const lfo = ctx.createOscillator();
        lfo.frequency.value = inst.vibrato.hz;
        const depth = ctx.createGain();
        // Vibrato arrives late, as a player would add it: nothing for the first beat, then it blooms.
        depth.gain.setValueAtTime(0, at);
        depth.gain.setTargetAtTime(inst.vibrato.cents, at + 0.18, 0.2);
        lfo.connect(depth).connect(osc.detune);
        lfo.start(at);
        lfo.stop(end);
      }
      osc.connect(head);
      osc.start(at);
      osc.stop(end);
    }
  }
}
